/* eslint-disable @typescript-eslint/no-unnecessary-condition */
/* eslint-disable no-restricted-syntax */

import { useMemo } from 'react'
import { appPaths } from '@config'
import type { IntroSectionData } from '@components/entities'
import type { FaqQnASectionProps } from '@components/feature/common'
import type { PreviewImageCardData } from '@components/feature/home/PreviewImageCard'
import type { ComparePlansData } from '@components/feature/pricing/ComparePlans'
import type { CreditSystemData } from '@components/feature/pricing/CreditSystem'
import type { OurPlansData } from '@components/feature/pricing/OurPlans'
import type { PricingPageData } from '@components/page/PricingPage'
import { SCROLL_ID } from '@utils/scroll'
import type { IconName } from 'src/media/resources/svg'

// New API Data Types

export enum PLAN_NAME_ENUM {
  GROWTH = 'Growth',
  PREMIUM = 'Premium',

  TRIAL = 'Trial',

  ENTERPRISE = 'Enterprise',
}

export interface PlanInfo {
  plan_name: PLAN_NAME_ENUM
  available: boolean
  value: string
}

export interface PlanInfoTableRow {
  feature_name: string
  feature_tool_tip: string
  plan_info: PlanInfo[]
}

export enum PRICE_TYPE {
  GROWTH = 'GROWTH',
  PREMIUM = 'PREMIUM',
  ENTERPRISE = 'ENTERPRISE',
}

// Type for the feature data structure you provided
export type FeaturePlanInfoWithType = {
  feature_plan_info_with_type: {
    [category: string]: Array<{
      feature_name: string
      feature_tool_tip?: string
      plan_info: Array<{
        plan_name: string
        available: boolean
        value: string
      }>
    }>
  }
}

// Function to transform your data structure to InfoTable format
export const transformFeatureDataForInfoTable = (
  data: FeaturePlanInfoWithType
): { category: string; data: PlanInfoTableRow[] }[] => {
  return Object.entries(data.feature_plan_info_with_type).map(
    ([category, features]) => ({
      category,
      data: features.map((feature) => ({
        feature_name: feature.feature_name,
        feature_tool_tip: feature.feature_tool_tip || '',
        plan_info: feature.plan_info.map((plan) => ({
          plan_name: plan.plan_name as PLAN_NAME_ENUM,
          available: plan.available,
          value: plan.value,
        })),
      })),
    })
  )
}

/**
 * Feature included in a plan - from API with include_features=true
 */
export type PlanFeature = {
  feature_id: string
  feature_name: string
  value: string
}

export type PricingTier = {
  plan_id: string
  price: number
  billing_cycle: 'MONTH' | 'ANNUAL'
  max_annual_spend: number
  created_at: number
  deleted_at: number | null
  id: string
  label_price: number
  currency: Currency
  min_annual_spend: number
  updated_at: number
  tax_rate: string
  subscription_plan_id: string
  credits: number
}

export type PlanCosts = {
  usd: PricingTier[]
  currency: PricingTier[]
}

export type PlanCard = {
  id: string
  description: string
  is_active: boolean
  trial_period: number
  renew_cycle: 'MONTH' | 'ANNUAL'
  updated_by: string | null
  deleted_at: number | null
  label: string
  name: string
  type: PlanType
  created_by: string | null
  created_at: number
  updated_at: number
  features: PlanFeature[]
  monthly_costs: PlanCosts
  annual_costs: PlanCosts
  plan_metadata: {
    credit_usage: {
      credits: number
      feature: string
    }[]
    quick_features: string[]
  }
}

export type PricingApiResponse = {
  cards: PlanCard[]
}

// Utility types for mapping
export type PlanType = 'GROWTH' | 'PREMIUM' | 'ENTERPRISE' | 'FREE' | 'CUSTOM'
export type Currency = 'USD' | 'INR'
export type BillingCycle = 'MONTH' | 'ANNUAL'

/**
 * Currency option returned from the currencies API
 */
export type CurrencyOption = {
  id: string
  code: string
  name: string
  symbol: string
}

/**
 * Dynamic Pricing API Response
 * Returned from /api/v1/plans/pricing
 */
export type ComparablePlan = {
  plan_id: string
  plan_name: string
  plan_type: PlanType
  cloud_spend_range_id: string
  max_annual_spend_usd: number
  price_usd: number
  label_price_usd: number
  price: number
  label_price: number
  currency: Currency
  monthly_price_for_annual_plan: number
  trial_period: number
}

export type ComparablePlans = Partial<
  Record<'growth_plan' | 'premium_plan', ComparablePlan>
>

export type DynamicPricingResponse = {
  // Core identifiers
  plan_id: string
  cloud_spend_range_id: string
  plan_name: string
  plan_type: PlanType

  // Pricing
  price_usd: number
  label_price_usd: number
  price: number
  label_price: number
  monthly_price_for_annual_plan: number
  discount: number
  currency: Currency

  // Spend & savings
  max_annual_spend: number
  projected_net_savings: number
  projected_net_saving_rate: number
  charge_rate: number

  // Credits & trial
  credits: number
  trial_period: number

  // Comparison
  comparable_plans: ComparablePlans
}

/**
 * Request params for dynamic pricing API
 */
export type DynamicPricingParams = {
  max_annual_spend: number
  currency?: Currency
  billing_cycle?: BillingCycle
}

/**
 * Icon mapping for quick features - hardcoded based on text patterns
 */
const QUICK_FEATURE_ICON_MAP: Record<string, IconName> = {
  cloud: 'Cloud',
  account: 'Cloud',
  credit: 'Star',
  upfront: 'Star',
  card: 'NoCard',
}

/**
 * Get icon for a quick feature based on text content
 */
const getQuickFeatureIcon = (text: string): IconName | undefined => {
  const textLower = text.toLowerCase()
  for (const [keyword, icon] of Object.entries(QUICK_FEATURE_ICON_MAP)) {
    if (textLower.includes(keyword)) {
      return icon
    }
  }
  return 'Star' // Default icon
}

/**
 * Checklist title mapping based on plan type - hardcoded
 */
const CHECKLIST_TITLE_MAP: Partial<Record<PlanType, string>> = {
  FREE: 'Free includes:',
  GROWTH: 'Growth Plan includes:',
  PREMIUM: 'Premium Plan includes:',
  ENTERPRISE: 'Enterprise Plan includes:',
}

/**
 * Type for processed plan features ready for display
 */
export type ProcessedPlanFeatures = {
  quick: Array<{ text: string; icon?: IconName }>
  credits: Array<{ text: string; value: string }>
  checklistTitle: string
  checklist: string[]
}

/**
 * Build plan features from API data
 *
 * @param planCard - The plan card data from API
 * @returns Processed features ready for display
 */
export const buildPlanFeatures = (
  planCard: PlanCard
): ProcessedPlanFeatures => {
  // Build quick features from plan_metadata.quick_features with hardcoded icons
  const quick = (planCard.plan_metadata?.quick_features || []).map((text) => ({
    text,
    icon: getQuickFeatureIcon(text),
  }))

  // Build credits from plan_metadata.credit_usage
  const credits = (planCard.plan_metadata?.credit_usage || []).map((item) => ({
    text: item.feature,
    value: `${item.credits} Credits`,
  }))

  // Build checklist from features array - format: "feature_name: value"
  const checklist = (planCard.features || [])
    .map((feature) => `${feature.feature_name}: ${feature.value}`)
    .slice(0, 6)

  // Get checklist title based on plan type
  const planType = planCard.type.toUpperCase() as PlanType
  const checklistTitle = CHECKLIST_TITLE_MAP[planType] || 'Includes:'

  return {
    quick,
    credits,
    checklistTitle,
    checklist,
  }
}

/**
 * Default features for fallback when API data is missing
 */
export const DEFAULT_PLAN_FEATURES: ProcessedPlanFeatures = {
  quick: [
    { text: '1 Cloud account included', icon: 'Cloud' },
    { text: 'All credits granted upfront', icon: 'Star' },
  ],
  credits: [
    { text: "Reveal contact's emails", value: '50 Credits' },
    { text: "Reveal contact's phones", value: '20 Credits' },
  ],
  checklistTitle: 'Includes:',
  checklist: [],
}

type GetPricingDataArgs = {
  /** Raw API data with cards */
  apiData?: PricingApiResponse
  /** Currency for price display */
  currency: Currency
  /** Billing cycle */
  billingCycle: BillingCycle
  /** Current plan type from dynamic pricing (for highlighting) */
  currentPlanType?: PlanType | null
  /** Full dynamic pricing response to override price for highlighted card */
  dynamicPricing?: DynamicPricingResponse | null
}

/**
 * Processed pricing card for UI display
 */
export type ProcessedPricingCard = {
  id: string
  title: string
  price: string | number
  currencySymbol: string
  features: ProcessedPlanFeatures
  buttonText: string
  buttonVariant: 'outlined' | 'contained'
  isRecommended: boolean
  highlight: boolean
  planType: PlanType
  credits: number
}

export const usePricingData = ({
  apiData,
  currency,
  billingCycle,
  currentPlanType,
  dynamicPricing,
}: GetPricingDataArgs) => {
  const pricingCards = useMemo(() => {
    const currencySymbol = currency === 'USD' ? '$' : '₹'

    const allCards: ProcessedPricingCard[] | undefined = apiData?.cards?.map(
      (card) => {
        // Build features from API data
        const features = buildPlanFeatures(card)

        // Determine button and highlight settings based on plan type
        const planType = card.type.toUpperCase() as PlanType
        let buttonVariant: 'outlined' | 'contained' = 'outlined'
        let isRecommended = false
        let buttonText = 'Choose Plan'

        if (planType === 'FREE') {
          buttonText = 'Get Started'
        } else if (planType === 'PREMIUM') {
          buttonVariant = 'contained'
          isRecommended = true
        } else if (planType === 'ENTERPRISE') {
          buttonText = 'Contact Our Sales'
        }

        // Get price from costs
        // If currency is USD, use costs.usd, otherwise use costs.currency
        const costs =
          billingCycle === 'MONTH' ? card.monthly_costs : card.annual_costs
        const currencyCosts = currency === 'USD' ? costs.usd : costs.currency
        let priceDisplay: string | number = currencyCosts?.[0]?.price ?? 0

        if (planType === 'FREE') {
          priceDisplay = 0
        }

        // For Enterprise, show "Custom" instead of price
        if (planType === 'ENTERPRISE') {
          priceDisplay = 'Custom'
        }

        // Highlight based on currentPlanType from context
        const highlight = planType === 'PREMIUM' // Default to Premium if no currentPlanType

        // Get credits from cost (use first tier as default)
        let credits = dynamicPricing?.credits ?? 0

        // For Enterprise, set credits to 500 (as shown in the screenshot)
        if (planType === 'ENTERPRISE') {
          credits = 500
        }

        // Use dynamic pricing data if available (for both Annual and Monthly)
        if (
          dynamicPricing &&
          planType !== 'FREE' &&
          planType !== 'ENTERPRISE'
        ) {
          // First, try to use comparable_plans if available
          if (dynamicPricing.comparable_plans) {
            const comparablePlanKey =
              planType === 'GROWTH' ? 'growth_plan' : 'premium_plan'
            const comparablePlan =
              dynamicPricing.comparable_plans[comparablePlanKey]

            if (comparablePlan) {
              // Use price_usd or price from comparable_plans
              priceDisplay =
                billingCycle === 'ANNUAL'
                  ? comparablePlan.monthly_price_for_annual_plan
                  : comparablePlan.price
            }
          }
          // If comparable_plans not available or plan not found, use direct pricing
          else if (dynamicPricing.plan_type === planType) {
            // Use price_usd or price from dynamic pricing directly
            priceDisplay =
              currency === 'USD'
                ? dynamicPricing.price_usd
                : dynamicPricing.price
          }
        }

        return {
          id: card.id,
          title: card.name,
          price: priceDisplay,
          currencySymbol,
          features,
          buttonText,
          buttonVariant,
          isRecommended,
          highlight,
          planType,
          credits,
        }
      }
    )

    // Desktop order: Free, Growth, Premium, Enterprise
    const desktopOrder: PlanType[] = ['FREE', 'GROWTH', 'PREMIUM', 'ENTERPRISE']
    const desktopSorted =
      allCards &&
      [...allCards].sort((a, b) => {
        const aIndex = desktopOrder.indexOf(a.planType)
        const bIndex = desktopOrder.indexOf(b.planType)
        return (aIndex === -1 ? 99 : aIndex) - (bIndex === -1 ? 99 : bIndex)
      })

    // Mobile order: Premium, Free, Growth, Enterprise (recommended first)
    const mobileOrder: PlanType[] = ['PREMIUM', 'FREE', 'GROWTH', 'ENTERPRISE']
    const mobileSorted =
      allCards &&
      [...allCards].sort((a, b) => {
        const aIndex = mobileOrder.indexOf(a.planType)
        const bIndex = mobileOrder.indexOf(b.planType)
        return (aIndex === -1 ? 99 : aIndex) - (bIndex === -1 ? 99 : bIndex)
      })

    return { desktop: desktopSorted, mobile: mobileSorted }
  }, [apiData, currency, billingCycle, currentPlanType, dynamicPricing])

  return {
    pricingCards: pricingCards.desktop,
    mobilePricingCards: pricingCards.mobile,
  }
}
export type QueryPricingPageData = {
  pricingPage: PricingPageData
}

export const introSectionData: IntroSectionData = {
  heading: 'Try our Standard plan for 50% off!',
  description:
    'From startups to enterprises, Cost Optimizer offers tailored solutions to suit every budget. Compare Plans and learn more about AI credits!',
  actionButtonData: [
    {
      children: 'Request a Demo',
      link: appPaths.bookDemo,
      variant: 'outlined',
    },
  ],
  bulletPointsData: [
    '10% Assured Saving on Billing Transfer',
    'Free L1 Support on Billing Transfer',
    'Agentic AI for Finops',
  ],
  aiCreditData: {
    title: 'Free AI Credits',
    creditAmount: 500,
    linkInfo: {
      label: 'How credits work?',
      link: `#${SCROLL_ID.CREDIT_SYSTEM}`,
    },
  },
}

export const faqSectionData: FaqQnASectionProps = {
  sectionHeaderData: {
    sectionTagData: { label: 'FAQ' },
    heading: 'Frequently Asked Questions',
    description:
      'If you can’t find the answer that you are looking for, feel free to drop us a line',
    actionButtonData: {
      children: 'Get in Touch',
      link: appPaths.contactUs,
    },
  },
  qnaAccordionData: [
    {
      title:
        "What's the difference between the Enterprise and Growth plan in terms of costing and features?",
      description:
        'The Growth plan is free, supports up to 5 users, and includes core cost tracking. Enterprise is a custom solution with pricing tailored to your scale. It includes unlimited users, advanced security (such as SSO/SAML), dedicated account management, and custom reporting.',
    },
    {
      title: 'Do I need to enter my credit card to start a free trial?',
      description:
        'No, absolutely not! You can start your 14-day Premium free trial immediately without entering any credit card information. We only ask for payment details if you decide to subscribe after your trial ends.',
    },
    {
      title: 'Are there any hidden fees or setup costs?',
      description:
        'Absolutely not. There are no setup fees, onboarding costs, or hidden charges. The price you see on the plan is the price you pay.',
    },
    {
      title: 'Do you offer a discount for paying annually?',
      description:
        'Yes! If you choose to pay annually, you get two months free, which is a 16% discount.',
    },
    {
      title: 'What support is included with my plan?',
      description:
        'Growth users get email support during business hours. Premium users get 24/7 priority chat and email support.',
    },
    {
      title: 'How do I contact your support team?',
      description:
        'The fastest way to reach us is by email at info@costimizer.ai. We strive to answer all priority inquiries within two hours.',
    },
    {
      title: 'Do you offer personalized onboarding?',
      description:
        'The Premium plan includes a free, guided onboarding session for your entire team to ensure a fast, successful setup.',
    },
    {
      title: 'What happens if I exceed my usage limits on the Growth plan?',
      description:
        'The Growth plan does not have usage limits; it’s limited by features and the number of users (up to 5). To unlock advanced features, simply upgrade to Premium.',
    },
    {
      title: 'Can I cancel or downgrade my subscription at any time?',
      description:
        'Yes. You can cancel or change your plan right in your account settings. If you cancel, your subscription remains active until the end of the current billing cycle.',
    },
    {
      title: 'Can I export my data at any time?',
      description:
        'Yes, absolutely. You can easily export all your data in standard CSV or JSON format with a single click, regardless of your subscription status or integration.',
    },
  ],
}

export const previewImageCardSectionData: PreviewImageCardData = {
  heading: 'Cut Costs, Not Corners 💸  ',
  description:
    'Maximize cloud efficiency with real-time insights and smarter spend management — all in one platform',
  actionButtonData: {
    link: '/',
    children: 'Start For Free',
  },
}

// Mapping utility functions
export const mapApiDataToPricingCards = (
  apiData: PricingApiResponse,
  currency: Currency = 'USD',
  billingCycle: BillingCycle = 'MONTH'
) => {
  return apiData.cards.map((card) => {
    const costs =
      billingCycle === 'MONTH' ? card.monthly_costs : card.annual_costs
    // If currency is USD, use costs.usd, otherwise use costs.currency
    const currencyCosts = currency === 'USD' ? costs.usd : costs.currency

    // Create options from pricing tiers
    const currencySymbol = currency === 'USD' ? '$' : '₹'
    const options =
      currencyCosts?.map((tier) => ({
        label: `${currencySymbol}${tier.min_annual_spend.toLocaleString()} - ${currencySymbol}${tier.max_annual_spend.toLocaleString()}`,
        value: tier.max_annual_spend,
        tier,
      })) || []

    return {
      id: card.id,
      title: card.name,
      description: card.description,
      type: card.type,
      trial_period: card.trial_period,
      options,
      costs: currencyCosts,
      // Default to first tier pricing
      defaultPricing: currencyCosts?.[0] || null,
    }
  })
}

// Helper function to get pricing for specific annual spend

type GetPricingForSpendArgs = {
  card: PlanCard
  annualSpend: number
  currency: Currency
  billingCycle: BillingCycle
}
export const getPricingForSpend = ({
  card,
  annualSpend,
  currency = 'USD',
  billingCycle = 'MONTH',
}: GetPricingForSpendArgs) => {
  const costs =
    billingCycle === 'MONTH' ? card.monthly_costs : card.annual_costs
  // If currency is USD, use costs.usd, otherwise use costs.currency
  const currencyCosts = currency === 'USD' ? costs.usd : costs.currency

  // Find the appropriate tier based on annual spend
  const tier = currencyCosts?.find(
    (t) =>
      annualSpend >= t.min_annual_spend && annualSpend <= t.max_annual_spend
  )

  return tier || currencyCosts?.[0] // Fallback to first tier
}

export const ourPlansData: OurPlansData = {
  sectionHeaderData: {
    sectionTagData: {
      label: 'OUR PLANS',
    },
    heading: 'See What We Offer!',
    description: 'Costimizer offers solutions for teams of all sizes and needs',
  },
}

export const comparePlansData: ComparePlansData = {
  sectionHeaderData: {
    sectionTagData: {
      label: 'COMPARE PLANS',
    },
    heading: 'See What We Offer!',
    description: 'Costimizer offers solutions for teams of all sizes and needs',
  },
}

export const creditSystemData: CreditSystemData = {
  sectionHeaderData: {
    actionButtonData: {
      children: 'Buy More Credit',
      variant: 'contained',
      link: appPaths.bookDemo,
    },
    sectionTagData: {
      label: 'COSTIMIZER CREDIT SYSTEM',
    },
    heading: 'How Our Credit System Works.',
    description:
      "Costimizer's AI credit system is designed for flexibility, transparency and efficiency - so you only pay for what you need, with no wasted credits.",
  },
  creditCardsData: [
    {
      title: "Contact's Phones Reveal",
      description:
        'Access verified phone data built solely for high connection rates.',
      actionButtonData: {
        children: '5 credits',
      },
    },
    {
      title: 'Export to CSV',
      description:
        'Access verified phone data built solely for high connection rates.',
      actionButtonData: {
        children: '10 credits',
      },
    },
    {
      title: 'Export to CRM',
      description:
        'Access verified phone data built solely for high connection rates.',
      actionButtonData: {
        children: '50 credits',
      },
    },
  ],
}
